import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BasicConfigSection from "./BasicConfigSection";
import ContentIncludeSection from "./ContentIncludeSection";
import DocumentFormatSection from "./DocumentFormatSection";
import ExamplesConfigSection from "./ExamplesConfigSection";
import IndexConfigSection from "./IndexConfigSection";
import LayoutMarginsSection from "./LayoutMarginsSection";
import StyleTypographySection from "./StyleTypographySection";

const ConfigForm = ({
  defaultLanguage,
  config,
  onConfigChange,
  updatePreset,
  showPreview,
  onGenerate,
  onReset,
  disabled,
}) => {
  const { t } = useTranslation();
  const isMarkdown = config.outputFormat === "markdown";
  const [fontSizeValues, setFontSizeValues] = useState({
    mainTitleFontSize: 18,
    ruleTitleFontSize: 13,
    ruleFontSize: 11,
    lineSpacing: 1.2,
  });
  const [isIncludeOpen, setIsIncludeOpen] = useState(true);

  const openPreview = () => {
    setIsIncludeOpen(false);
    showPreview();
  };

  // Mise à jour des valeurs des sliders
  useEffect(() => {
    setFontSizeValues({
      mainTitleFontSize: config.mainTitleFontSize || 18,
      ruleTitleFontSize: config.ruleTitleFontSize || 13,
      ruleFontSize: config.ruleFontSize || 11,
      lineSpacing: config.lineSpacing || 1.2,
    });
  }, [config]);

  const handleInputChange = (field, value) => {
    // Gérer les objets imbriqués (ex: "examples.inline")

    if (field.includes(".")) {
      const [parentKey, childKey] = field.split(".");
      const updatedConfig = {
        ...config,
        [parentKey]: {
          ...config[parentKey],
          [childKey]: value,
        },
      };
      onConfigChange(updatedConfig);
    }
    // Si field est "multipleFields" et value est un objet, on applique toutes les propriétés
    else if (field === "multipleFields" && typeof value === "object") {
      onConfigChange({ ...config, ...value });
    } else {
      // Toujours assigner la valeur au champ spécifié, qu'elle soit un objet ou non
      onConfigChange({ ...config, [field]: value });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onGenerate();
  };

  return (
    <div className="p-4 space-y-4 rounded-xl shadow-xl bg-background-100">
      {/* Header avec charte graphique */}
      <div
        className="p-6 text-center rounded-xl"
        style={{
          background:
            "linear-gradient(135deg, var(--accent-100), var(--accent-300))",
          color: "var(--text)",
          boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
        }}
      >
        <h2 className="mb-2 text-2xl font-bold">
          {t("pdfGenerator.config.title")}
        </h2>
        <p className="text-sm opacity-90">
          {t("pdfGenerator.config.subtitle")}
        </p>

        {/* Indicateur de sauvegarde automatique */}
        <div
          className="inline-flex gap-2 items-center px-4 py-2 mt-4 rounded-lg text-md"
          style={{
            backgroundColor: "rgba(255,255,255,0.2)",
            backdropFilter: "blur(10px)",
          }}
        >
          <span>💾</span>
          <span>{t("pdfGenerator.config.autoSave")}</span>
          <button
            type="button"
            onClick={onReset}
            disabled={disabled}
            className="px-2 py-1 ml-2 text-xs rounded transition-all hover:scale-105"
            style={{
              backgroundColor: "rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.3)",
            }}
            title={t("pdfGenerator.config.resetTooltip")}
          >
            {t("pdfGenerator.config.reset")}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-2">
        {/* Section 1: Configuration de base et Format - Côte à côte */}
        <div className="grid grid-cols-1 gap-x-2 lg:gap-x-4 md:grid-cols-2">
          <BasicConfigSection
            defaultLanguage={defaultLanguage}
            config={config}
            handleInputChange={handleInputChange}
            disabled={disabled}
            updatePreset={updatePreset}
          />
          <DocumentFormatSection
            config={config}
            handleInputChange={handleInputChange}
            disabled={disabled}
          />

          {/* Section 2: Mise en page et Style - Côte à côte */}
          {!isMarkdown && (
            <LayoutMarginsSection
              config={config}
              handleInputChange={handleInputChange}
              disabled={disabled}
            />
          )}
          {!isMarkdown && (
            <StyleTypographySection
              config={config}
              handleInputChange={handleInputChange}
              disabled={disabled}
            />
          )}

          {/* Section 3: Contenu à inclure */}
          <ContentIncludeSection
            className="md:col-span-2"
            config={config}
            handleInputChange={handleInputChange}
            disabled={disabled}
            isOpen={isIncludeOpen}
            setIsOpen={setIsIncludeOpen}
          />
          {/* Section 4: Configurations conditionnelles côte à côte */}
          {isIncludeOpen &&
            (config.includeExamples === true ||
              (!isMarkdown && config.includeIndex === true)) && (
              <>
                {/* Configuration des exemples */}
                {config.includeExamples !== false ? (
                  <div>
                    <ExamplesConfigSection
                      config={config}
                      handleInputChange={handleInputChange}
                      disabled={disabled}
                    />
                  </div>
                ) : (
                  <div className="p-6 text-center rounded-lg border border-dashed bg-background-100 border-secondary-300 text-text-500">
                    <div className="mb-3 text-4xl">💡</div>
                    <div className="mb-2 font-medium">
                      {t("sections.examples.disabled")}
                    </div>
                    <div className="text-sm">
                      {t("sections.examples.disabledMessage")}
                    </div>
                  </div>
                )}

                {!isMarkdown &&
                  (config.includeIndex === true ? (
                    <div>
                      <IndexConfigSection
                        config={config}
                        handleInputChange={handleInputChange}
                        disabled={disabled}
                      />
                    </div>
                  ) : (
                    <div
                      className="p-6 text-center rounded-lg"
                      style={{
                        backgroundColor: "var(--background-100)",
                        border: "1px dashed var(--accent-300)",
                        color: "var(--text-500)",
                      }}
                    >
                      <div className="mb-3 text-4xl">📑</div>
                      <div className="mb-2 font-medium">
                        {t("sections.index.disabled")}
                      </div>
                      <div className="text-sm">
                        {t("sections.index.disabledMessage")}
                      </div>
                    </div>
                  ))}
              </>
            )}
        </div>

        {/* Actions avec design moderne */}
        <div
          className="p-6 rounded-xl"
          style={{
            background:
              "linear-gradient(135deg, var(--secondary-50), var(--accent-50))",
            border: "1px solid var(--secondary-200)",
          }}
        >
          <div className="flex flex-col gap-4 sm:flex-row">
            {!isMarkdown && (
              <button
                type="button"
                onClick={openPreview}
                disabled={disabled}
                className="flex-1 px-6 py-3 text-lg rounded-xl transition-all duration-300 btn hover:scale-105"
                style={{
                  background:
                    "linear-gradient(135deg, var(--secondary-200), var(--secondary-400))",
                  color: "var(--text)",
                  border: "1px solid var(--secondary-600)",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                }}
              >
                {t("pdfGenerator.actions.preview")}
              </button>
            )}

            <button
              type="submit"
              disabled={disabled}
              className="flex-1 px-6 py-3 text-lg rounded-xl transition-all duration-300 btn hover:scale-105"
              style={{
                background:
                  "linear-gradient(135deg, var(--accent-200), var(--accent-400))",
                color: "var(--text)",
                border: "1px solid var(--accent-600)",
                boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              }}
            >
              {isMarkdown
                ? t("pdfGenerator.actions.generateMarkdown")
                : t("pdfGenerator.actions.generate")}
            </button>
          </div>

          {/* Indicateurs de statut */}
          <div className="flex gap-4 justify-center mt-4 text-sm">
            <div className="flex gap-2 items-center">
              <div
                className="w-2 h-2 rounded-full"
                style={{
                  backgroundColor:
                    config.includeProcedures !== false
                      ? "var(--primary-500)"
                      : "var(--text-300)",
                }}
              ></div>
              <span style={{ color: "var(--text-600)" }}>
                {t("pdfGenerator.status.procedures")}
              </span>
            </div>
            <div className="flex gap-2 items-center">
              <div
                className="w-2 h-2 rounded-full"
                style={{
                  backgroundColor:
                    config.includeExamples !== false
                      ? "var(--secondary-500)"
                      : "var(--text-300)",
                }}
              ></div>
              <span style={{ color: "var(--text-600)" }}>
                {t("pdfGenerator.status.examples")}
              </span>
            </div>
            {Number(config.version) >= 2026 && (
              <div className="flex gap-2 items-center">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor:
                      config.includeGlossary !== false
                        ? "var(--primary-500)"
                        : "var(--text-300)",
                  }}
                ></div>
                <span style={{ color: "var(--text-600)" }}>
                  {t("pdfGenerator.status.glossary")}
                </span>
              </div>
            )}
            {!isMarkdown && (
              <div className="flex gap-2 items-center">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor:
                      config.includeIndex === true
                        ? "var(--accent-500)"
                        : "var(--text-300)",
                  }}
                ></div>
                <span style={{ color: "var(--text-600)" }}>
                  {t("pdfGenerator.status.index")}
                </span>
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default ConfigForm;
